import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Pencil, Trash2, Copy, X, Save,
  FileText, Loader2, Package, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';


const ITEM_TYPE_KEYS = [
  { value: 'procedure', labelKey: 'provider.offerTemplates.itemProcedure' },
  { value: 'accommodation', labelKey: 'provider.offerTemplates.itemAccommodation' },
  { value: 'airport_transfer', labelKey: 'provider.offerTemplates.itemAirportTransfer' },
  { value: 'medication', labelKey: 'provider.offerTemplates.itemMedication' },
  { value: 'consultation', labelKey: 'provider.offerTemplates.itemConsultation' },
  { value: 'lab_tests', labelKey: 'provider.offerTemplates.itemLabTests' },
  { value: 'aftercare', labelKey: 'provider.offerTemplates.itemAftercare' },
  { value: 'other', labelKey: 'provider.offerTemplates.itemOther' },
];

const CURRENCIES = ['EUR', 'USD', 'GBP', 'TRY'];

function OfferTemplatesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const token = '';
  const headers = { 'Content-Type': 'application/json' };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clinic/offer-templates`);
      const data = res.data;
      if (data.success) setTemplates(data.templates || []);
    } catch (e) {
      toast.error(t('provider.offerTemplates.loadFailed'));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(t('provider.offerTemplates.confirmDelete', { name }))) return;
    try {
      await api.delete(`/clinic/offer-templates/${id}`);
      toast.success(t('provider.offerTemplates.deleted'));
      fetchTemplates();
    } catch { toast.error(t('provider.offerTemplates.deleteFailed')); }
  };

  if (editing) {
    return (
      <TemplateEditor
        templateId={editing === 'new' ? null : editing}
        headers={headers}
        onDone={() => { setEditing(null); fetchTemplates(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6" data-testid="offer-templates-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-bold text-[#122033] font-[Manrope]">{t('provider.offerTemplates.title')}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{t('provider.offerTemplates.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setEditing('new')} className="bg-[#3BA4C5] hover:bg-[#2d8aa8] text-white gap-1.5" data-testid="create-template-btn">
          <Plus className="h-3.5 w-3.5" /> {t('provider.offerTemplates.newTemplate')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-sm text-slate-400" data-testid="templates-loading">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('provider.offerTemplates.loading')}
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200" data-testid="templates-empty">
          <CardContent className="py-12 text-center">
            <Package className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">{t('provider.offerTemplates.noTemplates')}</p>
            <p className="text-xs text-slate-400 mt-1">{t('provider.offerTemplates.noTemplatesHint')}</p>
            <Button size="sm" variant="outline" onClick={() => setEditing('new')} className="mt-4 gap-1.5" data-testid="empty-create-btn">
              <Plus className="h-3.5 w-3.5" /> {t('provider.offerTemplates.createTemplate')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="templates-list">
          {templates.map(tpl => (
            <Card key={tpl.id} className="border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow" data-testid={`template-card-${tpl.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#3BA4C5] flex-shrink-0" />
                      <h3 className="text-sm font-semibold text-[#122033] truncate" data-testid="template-name">{tpl.template_name}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {tpl.treatment_slug && (
                        <Badge variant="secondary" className="text-[10px] h-5 bg-teal-50 text-teal-700">{tpl.treatment_slug.replace(/_/g, ' ')}</Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] h-5">{t('provider.offerTemplates.itemCount', { n: tpl.offer_items?.length || 0 })}</Badge>
                      <Badge variant="outline" className="text-[10px] h-5">{tpl.default_currency}</Badge>
                      {tpl.usage_count > 0 && (
                        <Badge variant="outline" className="text-[10px] h-5 text-amber-600 border-amber-200">{t('provider.offerTemplates.usedTimes', { n: tpl.usage_count })}</Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1.5">
                      {t('provider.offerTemplates.totalLabel', { currency: tpl.default_currency, amount: (tpl.offer_items || []).reduce((s, i) => s + (i.price || 0), 0).toLocaleString() })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button onClick={() => setEditing(tpl.id)} className="p-1.5 rounded-md text-slate-400 hover:text-[#3BA4C5] hover:bg-slate-50 transition-colors" data-testid="edit-template-btn">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deleteTemplate(tpl.id, tpl.template_name)} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid="delete-template-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ templateId, headers, onDone, onCancel }) {
  const { t } = useTranslation();
  const ITEM_TYPES = ITEM_TYPE_KEYS.map(x => ({ value: x.value, label: t(x.labelKey) }));
  const [form, setForm] = useState({
    template_name: '',
    treatment_slug: '',
    default_currency: 'EUR',
    offer_items: [{ item_type: 'procedure', description: '', price: '', currency: 'EUR' }],
  });
  const [saving, setSaving] = useState(false);
  const [loadingTpl, setLoadingTpl] = useState(!!templateId);

  useEffect(() => {
    if (!templateId) return;
    (async () => {
      try {
        const res = await api.get(`/clinic/offer-templates/${templateId}`);
        const data = res.data;
        if (data.success && data.template) {
          const tpl = data.template;
          setForm({
            template_name: tpl.template_name || '',
            treatment_slug: tpl.treatment_slug || '',
            default_currency: tpl.default_currency || 'EUR',
            offer_items: (tpl.offer_items || []).map(i => ({
              item_type: i.item_type || 'procedure',
              description: i.description || '',
              price: i.price?.toString() || '',
              currency: i.currency || tpl.default_currency || 'EUR',
            })),
          });
        }
      } catch { toast.error(t('provider.offerTemplates.loadDetailFailed')); }
      setLoadingTpl(false);
    })();
  }, [templateId, t]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addItem = () => setForm(p => ({ ...p, offer_items: [...p.offer_items, { item_type: 'procedure', description: '', price: '', currency: p.default_currency }] }));
  const updateItem = (i, field, value) => setForm(p => { const items = [...p.offer_items]; items[i] = { ...items[i], [field]: value }; return { ...p, offer_items: items }; });
  const removeItem = (i) => setForm(p => ({ ...p, offer_items: p.offer_items.filter((_, idx) => idx !== i) }));
  const total = form.offer_items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);

  const handleSave = async () => {
    if (!form.template_name.trim()) { toast.error(t('provider.offerTemplates.nameRequired')); return; }
    if (form.offer_items.length === 0) { toast.error(t('provider.offerTemplates.atLeastOneItem')); return; }
    const invalidItem = form.offer_items.find(i => !i.item_type || !i.price || parseFloat(i.price) <= 0);
    if (invalidItem) { toast.error(t('provider.offerTemplates.itemsInvalid')); return; }

    setSaving(true);
    try {
      const payload = {
        template_name: form.template_name.trim(),
        treatment_slug: form.treatment_slug.trim(),
        default_currency: form.default_currency,
        offer_items: form.offer_items.map(i => ({
          item_type: i.item_type,
          description: i.description,
          price: parseFloat(i.price) || 0,
          currency: i.currency || form.default_currency,
        })),
      };
      const url = templateId ? `/clinic/offer-templates/${templateId}` : `/clinic/offer-templates`;
      const method = templateId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data = res.data;
      if (data.success) { toast.success(templateId ? t('provider.offerTemplates.updated') : t('provider.offerTemplates.created')); onDone(); }
      else toast.error(data.message || t('provider.common.saveFailed'));
    } catch { toast.error(t('provider.common.saveFailed')); }
    setSaving(false);
  };

  if (loadingTpl) {
    return <div className="flex items-center justify-center py-16 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('provider.common.loadingEllipsis')}</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6" data-testid="template-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-[#122033] font-[Manrope]">{templateId ? t('provider.offerTemplates.editTemplate') : t('provider.offerTemplates.newTemplate')}</h2>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">{t('provider.offerTemplates.templateName')}</label>
          <input value={form.template_name} onChange={e => set('template_name', e.target.value)}
            placeholder={t('provider.offerTemplates.templateNamePlaceholder')}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#3BA4C5] focus:outline-none"
            data-testid="tpl-name-input" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">{t('provider.offerTemplates.treatment')}</label>
            <input value={form.treatment_slug} onChange={e => set('treatment_slug', e.target.value.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-').replace(/-+/g, '-'))}
              placeholder={t('provider.offerTemplates.treatmentPlaceholder')}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#3BA4C5] focus:outline-none"
              data-testid="tpl-treatment-input" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">{t('provider.offerTemplates.defaultCurrency')}</label>
            <select value={form.default_currency} onChange={e => set('default_currency', e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#3BA4C5] focus:outline-none"
              data-testid="tpl-currency-select">
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">{t('provider.offerTemplates.offerItems')}</label>
            <button onClick={addItem} className="text-[10px] text-[#3BA4C5] hover:text-[#2d8aa8] font-medium flex items-center gap-0.5" data-testid="tpl-add-item-btn">
              <Plus className="h-3 w-3" /> {t('provider.offerTemplates.addItem')}
            </button>
          </div>
          <div className="space-y-2" data-testid="tpl-items-list">
            {form.offer_items.map((item, i) => (
              <div key={i} className="flex gap-1.5 items-start p-2.5 bg-slate-50 rounded-lg border border-slate-200" data-testid={`tpl-item-${i}`}>
                <select value={item.item_type} onChange={e => updateItem(i, 'item_type', e.target.value)}
                  className="border border-slate-200 rounded px-2 py-1.5 text-xs w-28 flex-shrink-0">
                  {ITEM_TYPES.map(it => <option key={it.value} value={it.value}>{it.label}</option>)}
                </select>
                <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                  placeholder={t('provider.offerTemplates.descriptionPlaceholder')} className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-xs min-w-0" />
                <input type="number" min="0" step="0.01" value={item.price} onChange={e => updateItem(i, 'price', e.target.value)}
                  placeholder={t('provider.offerTemplates.pricePlaceholder')} className="border border-slate-200 rounded px-2 py-1.5 text-xs w-20" />
                <button onClick={() => removeItem(i)} className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0" data-testid={`tpl-remove-item-${i}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          {form.offer_items.length > 0 && (
            <p className="text-xs text-slate-500 mt-1.5 text-right font-medium" data-testid="tpl-total">
              {t('provider.offerTemplates.totalLabel', { currency: form.default_currency, amount: total.toLocaleString() })}
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#3BA4C5] hover:bg-[#2d8aa8] text-white gap-1.5" data-testid="tpl-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {templateId ? t('provider.offerTemplates.updateTemplate') : t('provider.offerTemplates.saveTemplate')}
          </Button>
          <Button variant="outline" onClick={onCancel} data-testid="tpl-cancel-btn">{t('provider.common.cancel')}</Button>
        </div>
      </div>
    </div>
  );
}

export default OfferTemplatesPage;
